import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useContext, useState } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    isAcceptedInvoiceUploadFile,
    putInvoiceFileToSignedUrl,
    signedUrlKindForFile,
    type InvoiceSignedUrlKind,
} from '../../utils/invoiceUploadFiles';

export const WEB_BULK_UPLOAD_MAX = 25;

export type BulkUploadProgress = {
    percent: number;
    message: string;
};

interface SignedUrlEntry {
    signedUrl: string;
    key: string;
}

interface BulkUploadResponse {
    bulkId: string;
    accepted: number;
    uuids: string[];
}

async function mapPool<T, R>(
    items: T[],
    concurrency: number,
    fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let nextIndex = 0;
    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
            const index = nextIndex++;
            results[index] = await fn(items[index], index);
        }
    });
    await Promise.all(workers);
    return results;
}

const bulkUploadInvoices = async (
    sessionId: string,
    payload: {
        files: File[];
        invoiceType: 'received' | 'returned' | 'return_slip' | 'sublet';
        locationId?: string;
        onProgress?: (progress: BulkUploadProgress) => void;
    },
): Promise<BulkUploadResponse> => {
    const headers = { Authorization: sessionId, 'Content-Type': 'application/json' };
    const files = payload.files;
    if (files.length === 0) {
        throw new Error('Please select at least one file.');
    }
    if (files.length > WEB_BULK_UPLOAD_MAX) {
        throw new Error(`You can upload at most ${WEB_BULK_UPLOAD_MAX} invoices at once.`);
    }

    const sequence: InvoiceSignedUrlKind[] = [];
    for (const file of files) {
        if (!isAcceptedInvoiceUploadFile(file)) {
            throw new Error('Only JPG, PNG, TIF, and PDF files are supported.');
        }
        const kind = signedUrlKindForFile(file);
        if (kind == null) {
            throw new Error('Only JPG, PNG, TIF, and PDF files are supported.');
        }
        sequence.push(kind);
    }

    payload.onProgress?.({ percent: 5, message: 'Requesting upload URLs…' });

    const signedUrlsRes = await axios.post(
        `${API_URL}/invoice/get-signed-urls`,
        { sequence },
        { headers, validateStatus: () => true },
    );
    if (signedUrlsRes.status !== 200) {
        throw new Error(signedUrlsRes.data?.error ?? 'Failed to get upload URLs');
    }

    const { urls } = signedUrlsRes.data as { urls: SignedUrlEntry[] };
    if (!urls || urls.length !== files.length) {
        throw new Error('Unexpected number of upload URLs returned');
    }

    payload.onProgress?.({ percent: 15, message: `Uploading 0 of ${files.length}…` });

    let completed = 0;
    const s3Keys = await mapPool(files, 3, async (file, index) => {
        await putInvoiceFileToSignedUrl(urls[index]!.signedUrl, file, sequence[index]!);
        completed += 1;
        payload.onProgress?.({
            percent: 15 + Math.round((completed / files.length) * 70),
            message: `Uploading ${completed} of ${files.length}…`,
        });
        return urls[index]!.key;
    });

    payload.onProgress?.({ percent: 90, message: 'Finishing…' });

    const uploadRes = await axios.post(
        `${API_URL}/invoice/bulk-upload`,
        {
            s3Keys,
            ...(payload.locationId ? { locationId: payload.locationId } : {}),
            invoiceType: payload.invoiceType,
        },
        { headers, validateStatus: () => true },
    );

    if (uploadRes.status !== 200) {
        const body = uploadRes.data as { error?: string };
        throw new Error(body?.error ?? 'Bulk upload failed');
    }

    payload.onProgress?.({ percent: 100, message: 'Done' });
    return uploadRes.data as BulkUploadResponse;
};

export const useBulkUploadInvoices = () => {
    const sessionContext = useContext(SessionContext);
    const [progress, setProgress] = useState<BulkUploadProgress | null>(null);

    const mutation = useMutation({
        onMutate: () => setProgress({ percent: 0, message: 'Starting…' }),
        onSettled: () => setProgress(null),
        mutationFn: (variables: {
            files: File[];
            invoiceType: 'received' | 'returned' | 'return_slip' | 'sublet';
            locationId?: string;
        }) =>
            bulkUploadInvoices(sessionContext?.sessionId ?? '', {
                ...variables,
                onProgress: setProgress,
            }),
    });

    return {
        ...mutation,
        progress,
    };
};
